import { useMemo, useState } from 'react'
import { Download, Info, Pencil, Plus, Upload } from 'lucide-react'
import {
  ConfirmDialog, CurrencyValue, EmptyState, FormDialog, ImportDialog, PageHeader, PermissionGate,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { bulkPaths } from '@/lib/bulk'
import { downloadFile } from '@/lib/api/client'
import { countryOptions, currencyOptions, timezoneOptions } from '@/lib/locale-options'
import {
  useCostCenters, useDesignations, useGrades, useLegalEntities, useLocations, useNextCode,
  useOrgMasterMutations,
} from '../api'

type Master = 'entities' | 'costCenters' | 'locations' | 'grades' | 'designations'

/** Each tab's label, and the URL segment its bulk endpoints live under. */
/**
 * @param singular written out rather than derived. Stripping a trailing "s" turned
 *     "Legal entities" into "legal entitie", which is what the New button and the dialog
 *     title both said. English plurals are not a string operation.
 * @param codePurpose the numbering series the server allocates from, or null where the
 *     codes are meaningful and should stay hand-written. Legal-entity codes here are HO,
 *     RTGT, TFTE — a generated series would be a step backwards.
 */
const TABS: {
  key: Master
  label: string
  singular: string
  segment: string
  codePurpose: string | null
}[] = [
  { key: 'entities', label: 'Legal entities', singular: 'legal entity', segment: 'legal-entities', codePurpose: null },
  { key: 'costCenters', label: 'Cost centres', singular: 'cost centre', segment: 'cost-centers', codePurpose: 'COST_CENTRE' },
  { key: 'locations', label: 'Locations', singular: 'location', segment: 'locations', codePurpose: 'LOCATION' },
  { key: 'grades', label: 'Grades', singular: 'grade', segment: 'grades', codePurpose: 'GRADE' },
  { key: 'designations', label: 'Designations', singular: 'designation', segment: 'designations', codePurpose: 'DESIGNATION' },
]

/** What one row of the dialog is editing, and which record it will write to. */
type Draft = Record<string, string> & { id?: string }

/**
 * The fields the five master views have between them.
 *
 * <p>Written out rather than reached through an index signature, because the edit dialog reads
 * whichever of them the current tab needs and an untyped bag turns a renamed field into a
 * silently blank input rather than a compile error.
 */
interface MasterRow {
  id: string
  code: string
  name: string
  description?: string | null
  active?: boolean
  level?: number
  minSalary?: number | null
  maxSalary?: number | null
  countryCode?: string | null
  baseCurrency?: string | null
  timezone?: string | null
  glCode?: string | null
  city?: string | null
  gradeId?: string | null
}

/**
 * The five masters that had no screen at all, and then had one that could only add.
 *
 * <p>Each follows the divisions rule: create, edit, deactivate, never delete — and
 * deactivation is refused while anybody still holds the row.
 *
 * <p>Edit is the part this page was missing. `updateLocation`, `updateGrade`,
 * `updateDesignation` and `updateCostCenter` had existed and been permissioned since the
 * masters were built, and were reachable from nothing: a typo in a job title could only be
 * fixed by creating a second job title.
 */
/**
 * @param only pins the page to one master and hides the tab strip.
 *
 * <p>Used by the dedicated Legal entities route. Legal entities were reachable only by
 * finding them as the first tab of a page called "Org masters", which is not where anybody
 * looks for the companies they employ people through — so they have their own side-menu
 * entry and their own URL, rendered by this same component so the two can never drift apart.
 */
export function OrgMastersPage({ only }: { only?: Master } = {}) {
  useBreadcrumb()
  const [tab, setTab] = useState<Master>(only ?? 'entities')

  const entities = useLegalEntities()
  const costCenters = useCostCenters()
  const locations = useLocations()
  const grades = useGrades()

  // Built once. Deriving ~250 countries and ~300 currencies from Intl is cheap but not free,
  // and doing it on every keystroke in the dialog would be felt.
  const countries = useMemo(() => countryOptions(document.documentElement.lang || 'en'), [])
  const currencies = useMemo(() => currencyOptions(document.documentElement.lang || 'en'), [])
  const timezones = useMemo(() => timezoneOptions(), [])

  const designations = useDesignations()
  const entityOptions = useLookup<{ id: string; name: string }>('legalEntities')
  const mutations = useOrgMasterMutations()

  const [draft, setDraft] = useState<Draft | null>(null)
  /**
   * Whether the person has typed in the Code field.
   *
   * <p>This is what makes a visible preview safe. When the field is untouched the save sends
   * a blank code and the server takes the next number from the series itself — so the number
   * is allocated once, at save, and a dialog that is opened and cancelled burns nothing. When
   * it has been edited, their code is sent and honoured.
   */
  const [codeEdited, setCodeEdited] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null)

  const current = TABS.find((entry) => entry.key === tab)!
  /**
   * The preview for a brand-new record of the current kind.
   *
   * <p>Asked for only while an unsaved dialog is open on a tab whose codes are numbered, so
   * it is one request per "New", not one per page load.
   */
  const previewingCode = draft != null && draft.id == null && current.codePurpose != null
  const nextCode = useNextCode(previewingCode ? current.codePurpose : null)

  /**
   * What the Code box shows: what was typed, or the preview while it is untouched.
   *
   * <p>Derived rather than copied into state by an effect. Writing the preview into the draft
   * meant a render, then a state write, then another render — and the value then had to be
   * un-written again to avoid sending it back. Deriving it keeps one source of truth: the
   * draft holds only what the person typed, and `codeToSend` can stay as simple as it is.
   */
  const shownCode = codeEdited || !previewingCode ? (draft?.code ?? '') : (nextCode.data ?? '')
  const singular = current.singular

  const act = (work: () => Promise<unknown>, fallback: string, inDialog = false) => {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await work()
      } catch (caught) {
        const message = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(message)
        else setError(message)
      }
    })()
  }

  function newDraft() {
    setError(null)
    setDialogError(null)
    setCodeEdited(false)
    setDraft(tab === 'grades'
      ? { code: '', name: '', description: '', level: '1', minSalary: '', maxSalary: '' }
      : tab === 'entities'
        ? { code: '', name: '', countryCode: '', baseCurrency: '', timezone: '' }
        : { code: '', name: '', extra: '' })
  }

  /** Opens the same dialog on an existing row, so add and edit cannot drift apart. */
  function editDraft(row: MasterRow) {
    setError(null)
    setDialogError(null)
    // An existing row's code is its identity in every import file and report that names it,
    // so it is never regenerated: treat it as already typed.
    setCodeEdited(true)
    const base = { id: row.id, code: row.code, name: row.name }
    setDraft(tab === 'grades'
      ? {
          ...base,
          description: row.description ?? '',
          level: String(row.level ?? 1),
          minSalary: row.minSalary == null ? '' : String(row.minSalary),
          maxSalary: row.maxSalary == null ? '' : String(row.maxSalary),
        }
      : tab === 'entities'
        ? {
            ...base,
            countryCode: row.countryCode ?? '',
            baseCurrency: row.baseCurrency ?? '',
            timezone: row.timezone ?? '',
          }
        : {
            ...base,
            extra: (tab === 'costCenters' ? row.glCode
              : tab === 'locations' ? row.city
                : row.gradeId) ?? '',
          })
  }

  /**
   * The code to send: blank when the preview was never edited, so the server allocates.
   *
   * <p>Sending the previewed string back would hand the same number to the next person who
   * opened the dialog, because a preview takes nothing from the series.
   */
  function codeToSend(): string {
    return codeEdited ? (draft?.code ?? '') : ''
  }

  function submit() {
    if (!draft) return
    const legalEntityId = entityOptions.data?.[0]?.id ?? null
    const editing = draft.id != null
    act(async () => {
      if (tab === 'entities') {
        const body = {
          code: draft.code, name: draft.name,
          baseCurrency: draft.baseCurrency || null, countryCode: draft.countryCode || null,
          timezone: draft.timezone || null, registrationNo: null, countryPack: null,
          address: null,
        }
        await (editing
          ? mutations.updateLegalEntity.mutateAsync({ id: draft.id!, ...body })
          : mutations.createLegalEntity.mutateAsync(body))
      } else if (tab === 'costCenters') {
        const body = {
          legalEntityId, code: codeToSend(), name: draft.name, glCode: draft.extra || null,
          ownerEmployeeId: null, active: true,
        }
        await (editing
          ? mutations.updateCostCenter.mutateAsync({ id: draft.id!, ...body })
          : mutations.createCostCenter.mutateAsync(body))
      } else if (tab === 'locations') {
        const body = {
          legalEntityId, code: codeToSend(), name: draft.name, city: draft.extra || null,
          address: null, countryCode: null, timezone: null, active: true,
        }
        await (editing
          ? mutations.updateLocation.mutateAsync({ id: draft.id!, ...body })
          : mutations.createLocation.mutateAsync(body))
      } else if (tab === 'grades') {
        const body = {
          code: codeToSend(), name: draft.name,
          // Empty string, not null: the server reads null as "not supplied, leave it alone"
          // so a bulk import cannot wipe descriptions, and "" as "clear it" — which is what
          // emptying the field on this screen means.
          description: draft.description ?? '',
          level: Number(draft.level || '1'),
          minSalary: draft.minSalary ? Number(draft.minSalary) : null,
          maxSalary: draft.maxSalary ? Number(draft.maxSalary) : null,
          active: true,
        }
        await (editing
          ? mutations.updateGrade.mutateAsync({ id: draft.id!, ...body })
          : mutations.createGrade.mutateAsync(body))
      } else {
        const body = {
          code: codeToSend(), name: draft.name, description: null,
          gradeId: draft.extra || null, active: true,
        }
        await (editing
          ? mutations.updateDesignation.mutateAsync({ id: draft.id!, ...body })
          : mutations.createDesignation.mutateAsync(body))
      }
      setDraft(null)
    }, 'That could not be saved', true)
  }

  function deactivate(id: string) {
    const call = tab === 'entities' ? mutations.deactivateLegalEntity
      : tab === 'costCenters' ? mutations.deactivateCostCenter
        : tab === 'locations' ? mutations.deactivateLocation
          : tab === 'grades' ? mutations.deactivateGrade
            : mutations.deactivateDesignation
    act(async () => {
      await call.mutateAsync(id)
      setRemoving(null)
    }, `That ${singular} could not be deactivated`)
  }

  /** The Edit / Deactivate pair, identical on every tab so the rules read the same way. */
  function RowActions({ row, active }: { row: MasterRow; active: boolean }) {
    return (
      <PermissionGate permission="org:write">
        <Button
          type="button" variant="ghost" size="sm"
          aria-label={`Edit ${row.name}`}
          onClick={() => editDraft(row)}
        >
          <Pencil className="size-4" aria-hidden="true" />
        </Button>
        {active && (
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => {
              setError(null)
              setRemoving({ id: row.id, name: row.name })
            }}
          >
            Deactivate
          </Button>
        )}
      </PermissionGate>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={only ? current.label : 'Org masters'}
        subtitle={only
          ? onlySubtitle(only)
          : 'Entities, cost centres, locations, grades and designations.'}
        actions={
          <PermissionGate permission="org:write">
            <Button
              type="button" variant="outline"
              onClick={() => {
                void downloadFile(
                  bulkPaths(`/v1/org/${current.segment}`).export,
                  `${current.segment}.xlsx`,
                )
              }}
            >
              <Download className="size-4" aria-hidden="true" />
              Export
            </Button>
            <Button type="button" variant="outline" onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden="true" />
              Import
            </Button>
            <Button type="button" onClick={newDraft}>
              <Plus className="size-4" aria-hidden="true" />
              New {singular}
            </Button>
          </PermissionGate>
        }
      />

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what={current.label.toLowerCase()}
        prefix={`/v1/org/${current.segment}`}
        invalidateKey={['org']}
      />

      {error && (
        <p role="alert" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        None of these can be deleted. A grade somebody was on two years ago is still referred
        to by their payslips; deactivating stops new use and keeps the history readable, and is
        refused while anybody still holds it. Importing a list you have already exported updates
        the rows it recognises by code rather than duplicating them.
      </p>

      {!only && (
      <nav className="flex flex-wrap gap-2" aria-label="Org masters">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            aria-current={tab === entry.key ? 'page' : undefined}
            className={`rounded-md px-3 py-1.5 text-sm ${
              tab === entry.key ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'bg-[var(--color-page)] text-[var(--color-body)] hover:bg-[var(--color-primary-soft)] hover:text-[var(--color-primary)]'
            }`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </nav>
      )}

      <Card>
        {tab === 'entities' && (
          <>
            {entities.isPending && <Skeleton className="m-4 h-20" />}
            {entities.isSuccess && entities.data.length === 0 && (
              <EmptyState title="No legal entity" description="Every employment contract is with one of these." />
            )}
            {entities.isSuccess && entities.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="p-3 text-start font-medium">Code</th>
                    <th className="p-3 text-start font-medium">Entity</th>
                    <th className="p-3 text-start font-medium">Country</th>
                    <th className="p-3 text-start font-medium">Currency</th>
                    <th className="p-3 text-start font-medium">Country pack</th>
                    <th className="p-3 text-end font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {entities.data.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100">
                      <td className="p-3 font-mono">{row.code}</td>
                      <td className="p-3">{row.name}</td>
                      <td className="p-3">{row.countryCode ?? '—'}</td>
                      <td className="p-3">{row.baseCurrency ?? '—'}</td>
                      <td className="p-3">{row.countryPack ?? 'Generic'}</td>
                      <td className="p-3 text-end">
                        <RowActions row={row} active />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === 'costCenters' && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-3 text-start font-medium">Code</th>
                <th className="p-3 text-start font-medium">Cost centre</th>
                <th className="p-3 text-start font-medium">GL code</th>
                <th className="p-3 text-end font-medium" />
              </tr>
            </thead>
            <tbody>
              {costCenters.data?.map((row) => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="p-3 font-mono">{row.code}</td>
                  <td className={`p-3 ${row.active ? '' : 'text-slate-400 line-through'}`}>
                    {row.name}
                  </td>
                  <td className="p-3">{row.glCode ?? '—'}</td>
                  <td className="p-3 text-end">
                    <RowActions row={row} active={row.active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'locations' && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-3 text-start font-medium">Code</th>
                <th className="p-3 text-start font-medium">Location</th>
                <th className="p-3 text-start font-medium">City</th>
                <th className="p-3 text-end font-medium">People</th>
                <th className="p-3 text-end font-medium" />
              </tr>
            </thead>
            <tbody>
              {locations.data?.map((row) => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="p-3 font-mono">{row.code}</td>
                  <td className={`p-3 ${row.active ? '' : 'text-slate-400 line-through'}`}>
                    {row.name}
                  </td>
                  <td className="p-3">{row.city ?? '—'}</td>
                  <td className="p-3 text-end">{row.headcount}</td>
                  <td className="p-3 text-end">
                    <RowActions row={row} active={row.active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'grades' && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-3 text-start font-medium">Code</th>
                <th className="p-3 text-start font-medium">Grade</th>
                <th className="p-3 text-end font-medium">Level</th>
                <th className="p-3 text-end font-medium">Band</th>
                <th className="p-3 text-end font-medium" />
              </tr>
            </thead>
            <tbody>
              {grades.data?.map((row) => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="p-3 font-mono">{row.code}</td>
                  <td className={`p-3 ${row.active ? '' : 'text-slate-400 line-through'}`}>
                    {row.name}
                  </td>
                  <td className="p-3 text-end">{row.level}</td>
                  <td className="p-3 text-end">
                    {row.minSalary == null && row.maxSalary == null
                      ? <span className="text-slate-500">No band set</span>
                      : <><CurrencyValue value={row.minSalary} compact /> – <CurrencyValue value={row.maxSalary} compact /></>}
                  </td>
                  <td className="p-3 text-end">
                    <RowActions row={row} active={row.active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'designations' && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-3 text-start font-medium">Code</th>
                <th className="p-3 text-start font-medium">Designation</th>
                <th className="p-3 text-start font-medium">Grade</th>
                <th className="p-3 text-end font-medium" />
              </tr>
            </thead>
            <tbody>
              {designations.data?.map((row) => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="p-3 font-mono">{row.code}</td>
                  <td className={`p-3 ${row.active ? '' : 'text-slate-400 line-through'}`}>
                    {row.name}
                  </td>
                  <td className="p-3">{row.gradeName ?? '—'}</td>
                  <td className="p-3 text-end">
                    <RowActions row={row} active={row.active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => { if (!open) setRemoving(null) }}
        title={`Deactivate ${removing?.name ?? ''}?`}
        description="It stops being offered for new use and stays on file, so anything that already refers to it keeps resolving. It is never deleted."
        confirmLabel="Deactivate"
        error={error}
        onConfirm={() => { if (removing) deactivate(removing.id) }}
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        title={`${draft?.id ? 'Edit' : 'New'} ${singular}`}
        submitLabel="Save"
        loading={false}
        error={dialogError}
        onSubmit={submit}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="master-code">Code</Label>
              <Input
                id="master-code" value={shownCode}
                onChange={(event) => {
                  setCodeEdited(true)
                  setDraft({ ...draft, code: event.target.value })
                }}
              />
              {current.codePurpose && !draft.id && (
                <p className="mt-1 text-xs text-slate-600">
                  {codeEdited
                    ? 'Your own code will be used.'
                    : 'Filled in for you. Type over it to use your own.'}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="master-name">Name</Label>
              <Input
                id="master-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            {tab === 'entities' && (
              <>
                <div>
                  <Label htmlFor="master-country">Country</Label>
                  <Select
                    id="master-country"
                    className="w-full"
                    value={draft.countryCode}
                    onChange={(event) => setDraft({ ...draft, countryCode: event.target.value })}
                  >
                    <option value="">Not set</option>
                    {countries.map((country) => (
                      <option key={country.value} value={country.value}>{country.label}</option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-slate-600">
                    It decides which statutory rules apply, so nothing picks it for you.
                  </p>
                </div>
                <div>
                  <Label htmlFor="master-currency">Base currency</Label>
                  <Select
                    id="master-currency"
                    className="w-full"
                    value={draft.baseCurrency}
                    onChange={(event) => setDraft({ ...draft, baseCurrency: event.target.value })}
                  >
                    <option value="">Not set</option>
                    {currencies.map((currency) => (
                      <option key={currency.value} value={currency.value}>{currency.label}</option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-slate-600">
                    Every salary and payslip this entity issues is in it.
                  </p>
                </div>
                <div>
                  <Label htmlFor="master-timezone">Timezone</Label>
                  <Select
                    id="master-timezone"
                    className="w-full"
                    value={draft.timezone}
                    onChange={(event) => setDraft({ ...draft, timezone: event.target.value })}
                  >
                    <option value="">Not set</option>
                    {timezones.map((zone) => (
                      <option key={zone.value} value={zone.value}>{zone.label}</option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-slate-600">
                    Attendance punches and cut-off times are read in it.
                  </p>
                </div>
              </>
            )}
            {tab === 'grades' && (
              <>
                <div className="sm:col-span-2">
                  <Label htmlFor="master-description">Description</Label>
                  <Textarea
                    id="master-description"
                    rows={3}
                    value={draft.description ?? ''}
                    onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    Who belongs in this grade and on what basis. Whoever places the next hire
                    reads this instead of copying whatever the last similar person had.
                  </p>
                </div>
                <div>
                  <Label htmlFor="master-level">Level</Label>
                  <Input
                    id="master-level" type="number" value={draft.level}
                    onChange={(event) => setDraft({ ...draft, level: event.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="master-min">Band minimum</Label>
                  <Input
                    id="master-min" type="number" value={draft.minSalary}
                    onChange={(event) => setDraft({ ...draft, minSalary: event.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="master-max">Band maximum</Label>
                  <Input
                    id="master-max" type="number" value={draft.maxSalary}
                    onChange={(event) => setDraft({ ...draft, maxSalary: event.target.value })}
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    A band the wrong way round is refused: no salary could satisfy it.
                  </p>
                </div>
              </>
            )}
            {tab !== 'entities' && tab !== 'grades' && (
              <div>
                <Label htmlFor="master-extra">
                  {tab === 'costCenters' ? 'GL code'
                    : tab === 'locations' ? 'City' : 'Grade'}
                </Label>
                {tab === 'designations' ? (
                  <Select
                    id="master-extra"
                    className="w-full"
                    value={draft.extra}
                    onChange={(event) => setDraft({ ...draft, extra: event.target.value })}
                  >
                    <option value="">No grade</option>
                    {grades.data?.map((grade) => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    id="master-extra" value={draft.extra}
                    onChange={(event) => setDraft({ ...draft, extra: event.target.value })}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </FormDialog>
    </div>
  )
}

/** The one-line explanation each master gets when it is shown on its own page. */
function onlySubtitle(only: Master): string {
  switch (only) {
    case 'entities':
      return 'The companies you employ people through. Every contract, payslip and WPS file is filed under one of these.'
    case 'costCenters':
      return 'Where a salary is charged. Reports and journals group by these.'
    case 'locations':
      return 'Where people work. Attendance and rosters are read per location.'
    case 'grades':
      return 'The pay structure: a level, a salary band and what the grade is for.'
    case 'designations':
      return 'Job titles, optionally tied to a grade.'
  }
}
